<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject, watch } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useDebounceFn } from "@vueuse/core"
import { useFetchWrapper } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import type { Services } from "@services"
import { ServiceError } from "@core/exceptions"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            validate: "Valider",
            resendCode: "Renvoyer un nouveau code",
            signOut: "Se déconnecter",
            alreadyValidated: "Votre compte est déjà validé ?",
            login: "Connectez-vous.",
            successMessage: "Compte validé. Redirection…",
            sendCodeSuccess: "Code envoyé par e-mail.",
            sendCodeError: "Erreur envoi",
            validateError:
                "Une erreur est survenue lors de la validation du compte.",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Identifiant utilisateur pour les appels API (validation + renvoi). */
        userId?: string
        /** E-mail utilisateur pour le renvoi de code (si userId absent). */
        userName?: string
    }>(),
    { userId: "", userName: "" }
)

const emit = defineEmits<{
    (
        e: "validationSuccess",
        result: {
            token?: string
            company?: { idcompany?: string }
            /** Code de validation utilisé (pour le parent first-login). */
            validationCode?: string
        }
    ): void
    (e: "signOut"): void
}>()

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const authStore = useAuthStore()
const { $updateUserAccountValidation, $sendUserAccountValidationMail } =
    inject<Services>("services")!

/** Récupère le code de validation depuis la query (validationCode ou validationcode). */
function getValidationCodeFromQuery(): string {
    const q = route.query as Record<string, string | string[] | undefined>
    const raw = q.validationCode ?? q.validationcode ?? ""
    const code =
        typeof raw === "string" ? raw : Array.isArray(raw) ? (raw[0] ?? "") : ""
    if (code?.length !== 6) return ""
    return code
}

// ================================
// STATE
// ================================
/** Code de validation saisi (ou issu de l'URL). */
const validationCode = ref(getValidationCodeFromQuery())
/** Afficher le bouton « Se déconnecter » (session expirée). */
const needLogout = ref(false)

/** Identifiant utilisateur effectif (prop ou route). */
const effectiveUserId = computed(
    () => props.userId || (route.params._id_user as string) || ""
)

/** Wrapper validation du compte. */
const {
    fetch: validateFetch,
    isFetching: isValidateFetching,
    hasError: hasValidateError,
    error: validateErrorMessage,
    isSuccess: isValidateSuccess,
} = useFetchWrapper(
    async () => {
        const result = (await $updateUserAccountValidation({
            userId: effectiveUserId.value,
            validationCode: validationCode.value,
        })) as { token?: string; company?: { idcompany?: string } }
        const token = result?.token
        if (token) {
            authStore.setToken(token)
            await authStore.fetchUser()

            emit("validationSuccess", {
                ...result,
                validationCode: validationCode.value,
            })
        }
    },
    (err: Error) => {
        const e = err as ServiceError
        needLogout.value = e.code === "required_no_auth"
    }
)

/** Wrapper renvoi du code par e-mail. */
const {
    fetch: sendCodeFetch,
    isFetching: isSendCodeFetching,
    hasError: hasSendCodeError,
    error: sendCodeErrorMessage,
    isSuccess: isSendCodeSuccess,
} = useFetchWrapper(async () => {
    await $sendUserAccountValidationMail({
        userId: effectiveUserId.value || undefined,
        userName: props.userName || undefined,
    })
})

/** Renvoi de code debounced (2 s) pour éviter les clics répétés. */
const debouncedSendCode = useDebounceFn(() => {
    sendCodeFetch()
}, 500)

/** Validation du compte debounced (2 s) pour éviter les clics répétés. */
const debouncedValidate = useDebounceFn(() => {
    validateFetch()
}, 500)

// ================================
// WATCHERS
// ================================
watch(
    () => getValidationCodeFromQuery(),
    (code) => {
        if (code) validationCode.value = code
    }
)

// ================================
// INTERACTIONS
// ================================
/** Soumet le formulaire de validation (debounced). */
function onSubmit(): void {
    if (!validationCode.value) return
    debouncedValidate()
}

/** Demande le renvoi du code par e-mail (debounced). */
function onSendCode(): void {
    debouncedSendCode()
}

/** Déconnecte l'utilisateur. */
function onClickSignOut(): void {
    emit("signOut")
}
</script>

<template>
    <form class="flex flex-col gap-4 w-full" @submit.prevent="onSubmit">
        <slot name="header" />
        <BaseInputOtp
            class="mx-auto"
            name="passcode"
            v-model="validationCode"
            :length="6"
            size="large"
            :disabled="isValidateSuccess"
        />
        <BaseButton
            type="submit"
            :label="t('validate')"
            :loading="isValidateFetching"
            :disabled="
                !validationCode ||
                validationCode.length !== 6 ||
                isValidateSuccess
            "
            class="w-full"
        />
        <BaseMessage v-if="isValidateSuccess" severity="success">
            {{ t("successMessage") }}
        </BaseMessage>
        <BaseMessage v-if="hasValidateError" severity="error">
            {{ validateErrorMessage?.message ?? t("validateError") }}
            <BaseButton
                v-if="needLogout"
                :label="t('signOut')"
                severity="danger"
                class="mt-2"
                @click="onClickSignOut"
            />
        </BaseMessage>
        <div class="flex flex-col gap-2" v-if="!isValidateSuccess">
            <BaseButton
                variant="text"
                size="small"
                :loading="isSendCodeFetching"
                :disabled="isSendCodeFetching"
                @click="onSendCode"
                :label="t('resendCode')"
            >
            </BaseButton>
            <BaseMessage
                v-if="hasSendCodeError || isSendCodeSuccess"
                :severity="hasSendCodeError ? 'error' : 'success'"
            >
                {{
                    hasSendCodeError
                        ? sendCodeErrorMessage
                        : t("sendCodeSuccess")
                }}
            </BaseMessage>
        </div>
    </form>
</template>
